/*
  Omega CRO page (sections/omega-cro-*.liquid).
  Quick view, receipt count-down, sound pads, Academy tabs, video modal, sticky bar,
  hardware prices in the visitor's currency.
  Self-contained: it reads only elements marked data-ocro-*, and Add to cart is left
  to the theme's own <product-form> (assets/product-form.js).
*/
(() => {
  if (window.OmegaCro) {
    window.OmegaCro.init(document);
    return;
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasIO = 'IntersectionObserver' in window;

  // Learns the visitor's money format from prices Liquid already rendered in their currency
  // (e.g. "€1.301,23" and "€391,95", or "$1,474" and "$444"), then formats whole units the same way.
  function moneyFormatFrom(samples) {
    const text = samples.find((s) => /\d/.test(s)) || '0';
    const m = text.trim().match(/^([^\d]*)([\d][\d.,'\s\u00a0\u202f\u2019]*\d|\d)([^\d]*)$/);
    if (!m) return (cents) => String(Math.round(cents / 100));
    const prefix = m[1];
    const suffix = m[3];
    let thousands = ',';
    for (const s of samples) {
      const num = (s.trim().match(/[\d][\d.,'\s\u00a0\u202f\u2019]*\d/) || [''])[0];
      const dec = num.match(/[.,](\d{2})$/);
      const body = dec ? num.slice(0, -3) : num;
      const sep = body.match(/\d([.,'\s\u00a0\u202f\u2019])\d{3}/);
      if (sep) { thousands = sep[1]; break; }
      if (dec) thousands = num.slice(-3, -2) === ',' ? '.' : ',';
    }
    return (cents) => prefix + String(Math.round(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + suffix;
  }

  // Video modal: buttons with data-ocro-video-open="<embed url>" open the section's [data-ocro-modal].
  function initVideo(section) {
    const modal = section.querySelector('[data-ocro-modal]');
    if (!modal) return;
    const frame = modal.querySelector('[data-ocro-modal-frame]');
    const closeButton = modal.querySelector('.ocro-modal__close');
    let opener = null;

    const close = () => {
      if (modal.hidden) return;
      modal.hidden = true;
      if (frame.querySelector('iframe')) frame.innerHTML = '';
      document.documentElement.classList.remove('ocro-noscroll');
      if (opener) opener.focus();
    };
    const open = (src, button) => {
      opener = button;
      if (src) {
        const iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.title = button.getAttribute('aria-label') || '';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        frame.innerHTML = '';
        frame.appendChild(iframe);
      }
      modal.hidden = false;
      document.documentElement.classList.add('ocro-noscroll');
      closeButton.focus();
    };

    section.querySelectorAll('[data-ocro-video-open]').forEach((button) => {
      button.addEventListener('click', () => open(button.getAttribute('data-ocro-video-open'), button));
    });
    modal.querySelectorAll('[data-ocro-modal-close]').forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });
  }

  // Quick view: a card opens its <template> in the panel under its row; ‹ › walk through every product.
  function initCollection(section) {
    const cards = Array.from(section.querySelectorAll('[data-ocro-card]'));
    if (!cards.length) return;
    const order = cards.map((card) => card.getAttribute('data-ocro-card'));
    const panels = Array.from(section.querySelectorAll('[data-ocro-qv]'));

    const closeAll = () => {
      panels.forEach((panel) => {
        panel.hidden = true;
        panel.removeAttribute('data-current');
      });
      cards.forEach((card) => {
        card.setAttribute('aria-expanded', 'false');
        card.classList.remove('is-active');
      });
    };

    // nav: { panel, selector, top } when opened with ‹ ›, so that button can stay under the pointer
    const open = (id, nav) => {
      const card = section.querySelector(`[data-ocro-card="${id}"]`);
      const template = section.querySelector(`template[data-ocro-qv-content="${id}"]`);
      if (!card || !template) return;
      const panel = card.closest('[data-ocro-group]').querySelector('[data-ocro-qv]');
      panels.forEach((p) => {
        if (p !== panel) p.hidden = true;
      });
      cards.forEach((c) => {
        const active = c === card;
        c.setAttribute('aria-expanded', active ? 'true' : 'false');
        c.classList.toggle('is-active', active);
      });
      panel.querySelector('[data-ocro-qv-body]').replaceChildren(template.content.cloneNode(true));
      panel.setAttribute('data-current', id);
      panel.hidden = false;
      panel.classList.remove('is-in');
      void panel.offsetWidth;
      panel.classList.add('is-in');

      const rail = card.closest('.ocro-rail');
      if (rail && rail.scrollWidth > rail.clientWidth) {
        rail.scrollTo({ left: card.offsetLeft - 16, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      // ‹ › within the same row: the pressed button stays where it was, so it can be pressed again
      if (nav && nav.panel === panel) {
        const shift = panel.querySelector(nav.selector).getBoundingClientRect().top - nav.top;
        if (Math.abs(shift) > 1) window.scrollBy(0, shift);
        return;
      }
      // otherwise show the panel above the sticky Add to cart bar: all of it if it fits, else from its top
      const sticky = document.querySelector('[data-ocro-sticky].is-visible');
      const room = window.innerHeight - (sticky ? sticky.getBoundingClientRect().height + 16 : 0);
      const rect = panel.getBoundingClientRect();
      if (rect.bottom > room || rect.top < 0) {
        const delta = rect.height > room - 16 || rect.top < 0 ? rect.top - 16 : rect.bottom - room;
        window.scrollBy({ top: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    };

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        if (card.classList.contains('is-active')) closeAll();
        else open(card.getAttribute('data-ocro-card'), null);
      });
    });
    panels.forEach((panel) => {
      const step = (delta) => {
        const index = order.indexOf(panel.getAttribute('data-current'));
        if (index < 0) return;
        const selector = delta > 0 ? '[data-ocro-qv-next]' : '[data-ocro-qv-prev]';
        const top = panel.querySelector(selector).getBoundingClientRect().top;
        open(order[(index + delta + order.length) % order.length], { panel, selector, top });
      };
      panel.querySelector('[data-ocro-qv-close]').addEventListener('click', () => {
        const id = panel.getAttribute('data-current');
        closeAll();
        const card = section.querySelector(`[data-ocro-card="${id}"]`);
        if (card) card.focus();
      });
      panel.querySelector('[data-ocro-qv-prev]').addEventListener('click', () => step(-1));
      panel.querySelector('[data-ocro-qv-next]').addEventListener('click', () => step(1));
    });
  }

  // Receipt: counts down from the bought-separately total to the bundle price when it scrolls into view.
  function initReceipt(section) {
    const receipt = section.querySelector('[data-ocro-receipt]');
    if (!receipt || !hasIO || reduceMotion) return;
    const from = Number(receipt.getAttribute('data-from'));
    const to = Number(receipt.getAttribute('data-to'));
    const price = receipt.querySelector('[data-ocro-receipt-price]');
    if (!price || !(from > to)) return;
    const finalText = price.textContent;
    const total = receipt.querySelector('.ocro-receipt__total');
    const money = moneyFormatFrom([total ? total.textContent : '', finalText]);
    let timer = null;
    let frame = null;
    let inView = false;

    const reset = () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      receipt.classList.remove('is-counting', 'is-done');
      price.textContent = money(from);
    };
    const play = () => {
      reset();
      timer = setTimeout(() => {
        receipt.classList.add('is-counting');
        const start = performance.now();
        const duration = 1500;
        const tick = (now) => {
          const p = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          if (p < 1) {
            price.textContent = money(from - (from - to) * eased);
            frame = requestAnimationFrame(tick);
          } else {
            price.textContent = finalText;
            receipt.classList.add('is-done');
          }
        };
        frame = requestAnimationFrame(tick);
      }, 400);
    };

    receipt.classList.add('is-ready');
    reset();
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.5 && !inView) {
            inView = true;
            play();
          } else if (entry.intersectionRatio === 0 && inView) {
            inView = false;
            reset();
          }
        });
      },
      { threshold: [0, 0.5] }
    ).observe(receipt);
  }

  // Hardware prices are typed in US dollars. When the visitor pays in another currency, the section
  // carries data-ocro-convert and a sample price in their format; every [data-ocro-usd] amount is then
  // converted at Shopify's own rate (window.Shopify.currency.rate) and rounded to whole units.
  // Row totals and the heading total are sums of the rounded card prices, so they always add up.
  function initHardware(section) {
    if (!section.hasAttribute('data-ocro-convert')) return;
    const currency = window.Shopify && window.Shopify.currency;
    const rate = Number(currency && currency.rate);
    if (!(rate > 0)) return;
    const money = moneyFormatFrom([section.getAttribute('data-ocro-money-sample') || '']);
    const local = (usd) => Math.round(usd * rate);
    const rows = Array.from(section.querySelectorAll('.ocro-hwrow'));
    let grand = 0;
    rows.forEach((row) => {
      let rowSum = 0;
      row.querySelectorAll('.ocro-marquee__set:not([aria-hidden]) .ocro-hwcard [data-ocro-usd]').forEach((el) => {
        rowSum += local(Number(el.getAttribute('data-ocro-usd')));
      });
      row.querySelectorAll('.ocro-hwcard [data-ocro-usd]').forEach((el) => {
        el.textContent = money(local(Number(el.getAttribute('data-ocro-usd'))) * 100);
      });
      const total = row.querySelector('.ocro-hwrow__total[data-ocro-usd]');
      if (total) total.textContent = money(rowSum * 100);
      grand += rowSum;
    });
    section.querySelectorAll('.ocro-hardware__head [data-ocro-usd]').forEach((el) => {
      el.textContent = money(Math.floor(grand / 1000) * 1000 * 100);
    });
  }

  // Sound pads: one shared audio element; a pad plays, another switches, the same one pauses.
  function initPads(section) {
    const pads = Array.from(section.querySelectorAll('[data-ocro-pad]'));
    if (!pads.length) return;
    const audio = new Audio();
    audio.preload = 'none';
    let current = null;
    let scrubbing = null; // the pad whose waveform is being dragged

    pads.forEach((pad, i) => {
      const wave = pad.querySelector('[data-ocro-wave]');
      if (wave && !wave.children.length) {
        const seed = i * 1.7 + 0.4;
        for (let j = 0; j < 32; j++) {
          const v = Math.abs(Math.sin(j * 0.9 + seed) * 0.6 + Math.sin(j * 0.37 + seed * 2) * 0.4);
          const bar = document.createElement('span');
          bar.style.height = `${Math.max(10, Math.round(v * 100))}%`;
          bar.style.animationDuration = `${0.6 + (j % 5) * 0.12}s`;
          wave.appendChild(bar);
        }
      }
      pad.addEventListener('click', () => {
        const src = pad.getAttribute('data-ocro-pad');
        if (!src) return;
        if (current === pad) {
          if (audio.paused) play();
          else audio.pause();
          return;
        }
        current = pad;
        audio.src = src;
        play();
        render();
      });

      // Click, tap or drag on the waveform to jump to that point (it starts the pad if needed).
      // Vertical swipes on phones still scroll the page (touch-action: pan-y in the CSS).
      if (!wave) return;
      wave.addEventListener('click', (event) => event.stopPropagation());
      const ratioAt = (event) => {
        const box = wave.getBoundingClientRect();
        return Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
      };
      wave.addEventListener('pointerdown', (event) => {
        if (event.button > 0 || !pad.getAttribute('data-ocro-pad')) return;
        scrubbing = pad;
        wave.setPointerCapture(event.pointerId);
        progress(pad, ratioAt(event));
      });
      wave.addEventListener('pointermove', (event) => {
        if (scrubbing === pad) progress(pad, ratioAt(event));
      });
      wave.addEventListener('pointerup', (event) => {
        if (scrubbing !== pad) return;
        scrubbing = null;
        seek(pad, ratioAt(event));
      });
      wave.addEventListener('pointercancel', () => {
        scrubbing = null;
        progress(pad, pad === current && audio.duration ? audio.currentTime / audio.duration : 0);
      });

      // Keyboard: arrow keys skip 5 seconds in the pad that is playing.
      pad.addEventListener('keydown', (event) => {
        if (pad !== current || !audio.duration || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
        event.preventDefault();
        const step = event.key === 'ArrowRight' ? 5 : -5;
        seek(pad, Math.min(audio.duration, Math.max(0, audio.currentTime + step)) / audio.duration);
      });
    });

    function play() {
      const promise = audio.play();
      if (promise) promise.catch(() => {});
    }

    function seek(pad, ratio) {
      progress(pad, ratio);
      const jump = () => {
        if (audio.duration) audio.currentTime = ratio * audio.duration;
      };
      if (pad !== current) {
        current = pad;
        audio.src = pad.getAttribute('data-ocro-pad');
        audio.addEventListener('loadedmetadata', jump, { once: true });
        play();
        render();
        return;
      }
      if (audio.duration) jump();
      else audio.addEventListener('loadedmetadata', jump, { once: true });
      if (audio.paused) play();
    }

    // Real waveforms (from snippets/omega-cro-peaks): bars light up as the sound plays.
    function progress(pad, ratio) {
      const bars = pad.querySelectorAll('.ocro-pad__wave--real span');
      const lit = Math.round(ratio * bars.length);
      bars.forEach((bar, j) => bar.classList.toggle('is-on', j < lit));
    }
    let frame = 0;
    const tick = () => {
      if (current && audio.duration && scrubbing !== current) progress(current, audio.currentTime / audio.duration);
      frame = audio.paused ? 0 : requestAnimationFrame(tick);
    };

    const render = () => {
      pads.forEach((pad) => {
        const playing = pad === current && !audio.paused;
        pad.classList.toggle('is-playing', playing);
        pad.setAttribute('aria-pressed', playing ? 'true' : 'false');
        const state = pad.querySelector('.ocro-pad__state');
        if (state) state.textContent = state.getAttribute(playing ? 'data-playing' : 'data-play');
        if (pad !== current) progress(pad, 0);
      });
      if (!audio.paused && !frame) frame = requestAnimationFrame(tick);
    };
    ['play', 'pause'].forEach((type) => audio.addEventListener(type, render));
    audio.addEventListener('seeked', () => {
      if (current && audio.duration) progress(current, audio.currentTime / audio.duration);
    });
    audio.addEventListener('ended', () => {
      if (current) progress(current, 0);
      render();
    });
  }

  // Academy topic tabs.
  function initTabs(section) {
    section.querySelectorAll('[data-ocro-tabs]').forEach((root) => {
      const tabs = Array.from(root.querySelectorAll('[data-ocro-tab]'));
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const active = t === tab;
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          t.tabIndex = active ? 0 : -1;
          const panel = root.querySelector(`#${t.getAttribute('aria-controls')}`);
          if (panel) panel.hidden = !active;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab, false));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const next = tabs[(i + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
          select(next, true);
        });
      });
    });
  }

  // Sticky Add to cart: shows once the hero buttons scroll away, hides from the final call to action down.
  function initSticky(section) {
    const bar = section.querySelector('[data-ocro-sticky]');
    const cta = section.querySelector('[data-ocro-hero-cta]');
    if (!bar || !cta || !hasIO) return;
    const finalSection = document.querySelector('[data-ocro-final]');
    let ctaGone = false;
    let finalReached = false;

    const update = () => {
      const show = ctaGone && !finalReached;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.inert = !show;
      // hides the chat button while the bar is up (see omega-cro.css)
      document.documentElement.classList.toggle('ocro-sticky-on', show);
      // theme preview only: sit above Shopify's preview bar instead of under it
      const previewBar = document.getElementById('PBarNextFrameWrapper');
      bar.style.bottom = previewBar && previewBar.offsetHeight ? `${previewBar.offsetHeight}px` : '';
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === cta) ctaGone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        if (entry.target === finalSection) finalReached = entry.isIntersecting || entry.boundingClientRect.top < 0;
      });
      update();
    });
    observer.observe(cta);
    if (finalSection) observer.observe(finalSection);
    update();

    // section menu (wide screens): highlights the last section whose top has passed a third of the screen
    const nav = bar.querySelector('[data-ocro-nav]');
    if (!nav) return;
    const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
    const targets = links.map((link) => document.querySelector(link.getAttribute('href')));
    let ticking = false;
    const mark = () => {
      ticking = false;
      const line = window.innerHeight * 0.35;
      let active = -1;
      targets.forEach((target, i) => {
        if (target && target.getBoundingClientRect().top <= line) active = i;
      });
      links.forEach((link, i) => {
        link.classList.toggle('is-active', i === active);
        if (i === active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(mark);
    }, { passive: true });
    mark();
  }

  // FAQ: the first answer starts open on desktop only, to keep the phone page short.
  function initFaq(section) {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    section.querySelectorAll('details[data-ocro-open-desktop]').forEach((item) => (item.open = false));
  }

  // Reviews rail: arrows scroll one card at a time; touch and trackpads scroll natively.
  function initReviewRail(section) {
    const rail = section.querySelector('[data-ocro-rv-rail]');
    if (!rail) return;
    const step = (dir) => {
      const card = rail.querySelector('.ocro-rv');
      const gap = 16;
      const by = card ? card.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
      rail.scrollBy({ left: dir * by, behavior: reduceMotion ? 'auto' : 'smooth' });
    };
    const prev = section.querySelector('[data-ocro-rv-prev]');
    const next = section.querySelector('[data-ocro-rv-next]');
    const sync = () => {
      const max = rail.scrollWidth - rail.clientWidth - 2;
      if (prev) prev.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft >= max;
    };
    if (prev) prev.addEventListener('click', () => step(-1));
    if (next) next.addEventListener('click', () => step(1));
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  const handlers = {
    hero: [initVideo, initSticky],
    collection: [initCollection, initReceipt],
    sounds: [initPads],
    hardware: [initHardware],
    academy: [initVideo, initTabs],
    faq: [initFaq],
    reviews: [initReviewRail],
  };

  function init(root) {
    (root || document).querySelectorAll('[data-ocro]').forEach((section) => {
      if (section.hasAttribute('data-ocro-ready')) return;
      section.setAttribute('data-ocro-ready', '');
      (handlers[section.getAttribute('data-ocro')] || []).forEach((fn) => fn(section));
    });
  }

  window.OmegaCro = { init };
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
